import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { api } from "@/lib/api";

// Local portal backend — owns the cross-device `seen` sync collection so
// clearing the badge on the laptop also clears it on the phone.
const PORTAL_BACKEND = process.env.REACT_APP_BACKEND_URL;
const portalApi = axios.create({ baseURL: PORTAL_BACKEND });

/**
 * useSignoffInbox — polls the Pit Link feeds and surfaces items awaiting a
 * supervisor / owner's attention from the current user's role. This is the
 * true "notifications" feed: what needs my attention right now.
 *
 * Currently tracked:
 *   • Inspections  where status === "submitted"                (SIGN)
 *   • PPFs         where status === "pending signing"          (SIGN)
 *   • Breakdowns   where finalised but not yet approved        (SIGN)
 *   • Hazards      where assigned supervisor hasn't signed off (SIGN)
 *   • Handovers    where a fresh handover was submitted        (INFO — no sign required)
 *
 * `informational: true` sources show a "VIEW" pill instead of "SIGN" — the
 * supervisor just gets a heads-up that something was lodged.
 */

const SUP_ROLES = ["admin", "control", "owner", "supervisor", "superintendent", "planner"];

// Handovers stay in the inbox for this long after submission. Beyond this
// window they're considered "old news" and dropped from the notifications.
const HANDOVER_NOTIFY_WINDOW_MS = 24 * 60 * 60 * 1000; // 24h

const SOURCES = [
  {
    kind: "inspection",
    endpoint: "/inspections",
    label: "Inspection",
    route: "/inspections",
    verb: "Approve",
    needsSign: (x) => {
      const s = String(x.status || x.stage || "").toLowerCase();
      return /submit|ready|pending|await|review/.test(s);
    },
    time: (x) => x.submitted_at || x.updated_at || x.created_at,
    actor: (x) => x.started_by_name || x.inspector_name || x.created_by_name || x.user,
    subject: (x) => x.asset_id || x.asset_name || x.template_name || "Inspection",
    // Tradesman → supervisor routing. When a specific supervisor is
    // designated (via the "send to" step during Ready-to-sign), only that
    // supervisor sees it. Owners/admins do NOT see items routed to another
    // named supervisor — this is what the user complained about.
    assignedTo: (x) => x.supervisor_id || x.assigned_supervisor_id || null,
    assignedToName: (x) => x.supervisor_name || x.assigned_supervisor_name || null,
    assignedToUsername: (x) => x.supervisor_username || x.assigned_supervisor_username || null,
  },
  {
    kind: "ppf",
    endpoint: "/ppfs",
    label: "Planning Request",
    route: "/ppfs",
    verb: "Sign & Complete",
    needsSign: (x) => {
      const s = String(x.status || x.stage || "").toLowerCase();
      return /pending|submit|await|review/.test(s);
    },
    time: (x) => x.submitted_at || x.updated_at || x.created_at,
    actor: (x) => x.created_by_name || x.author || x.reported_by,
    subject: (x) => `${x.asset_id || x.asset_name || "Planning Request"} · ${(x.fault || x.description || "").slice(0, 40)}`,
    assignedTo: (x) => x.supervisor_id || x.target_supervisor_id || null,
    assignedToName: (x) => x.supervisor_name || x.target_supervisor_name || null,
    assignedToUsername: (x) => x.supervisor_username || null,
  },
  {
    kind: "breakdown",
    endpoint: "/breakdowns",
    label: "Breakdown",
    route: "/breakdowns",
    verb: "Approve",
    needsSign: (x) => {
      // A breakdown needs a supervisor signature when the tradesman has
      // finalised the report but a supervisor has not yet approved it.
      if (x.approved_at) return false;
      if (x.supervisor_sig) return false;
      if (x.is_finalised) return true;
      const s = String(x.status || "").toLowerCase();
      return /submit|await|pending|review/.test(s);
    },
    time: (x) => x.breakdown_time || x.updated_at || x.created_at,
    actor: (x) => x.employee_name || x.tradesman_name || x.created_by_name,
    subject: (x) => `${x.asset_id || x.asset_name || "Breakdown"} · ${(x.complaint || x.cause || "").slice(0, 40)}`,
    assignedTo: (x) => x.assigned_supervisor_id || x.supervisor_id || null,
    assignedToName: (x) => x.assigned_supervisor_name || x.supervisor_name || null,
    assignedToUsername: (x) => x.assigned_supervisor_username || x.supervisor_username || null,
  },
  {
    kind: "hazard",
    endpoint: "/hazards/pending",
    label: "Hazard",
    route: "/hazards",
    verb: "Sign Off",
    needsSign: (x) => {
      // A hazard needs the assigned supervisor to sign it off. Once "actioned"
      // (status set by /hazards/{id}/action), the record stays in the list
      // but no longer needs sign-off.
      const s = String(x.status || "").toLowerCase();
      return s === "pending" || s === "submitted" || s === "";
    },
    time: (x) => x.created_at || x.reported_at || x.ts,
    actor: (x) => x.tradesman_name || x.created_by_name || x.reported_to,
    subject: (x) => `${(x.risk_level || "hazard").toUpperCase()} · ${(x.description || x.location || "").slice(0, 60)}`,
    assignedTo: (x) => x.assigned_supervisor_id || null,
    assignedToName: (x) => x.assigned_supervisor_name || null,
    assignedToUsername: (x) => x.assigned_supervisor_username || null,
  },
  {
    kind: "handover",
    endpoint: "/handovers",
    label: "Handover",
    route: "/handovers",
    verb: "View",
    // Informational-only: no sign-off contract. Supervisor just needs a
    // heads-up that a fresh handover has been lodged from someone on their
    // crew — so they know shift-summary numbers, safety issues, and priorities
    // for the next crew are all in.
    informational: true,
    needsSign: (x) => {
      // Only surface freshly-lodged handovers — must have a signature (so it's
      // been "submitted", not a draft) and be within the notify window.
      const signed = !!(x.sign_off_signature || x.sign_off_name);
      if (!signed) return false;
      const ts = new Date(x.created_at || x.date || 0).getTime();
      if (!ts) return false;
      return (Date.now() - ts) < HANDOVER_NOTIFY_WINDOW_MS;
    },
    time: (x) => x.created_at || x.date || x.ts,
    actor: (x) => x.tradesman || x.sign_off_name || x.created_by_name,
    subject: (x) => `${x.asset_id || "Handover"} · ${(x.work_completed || x.next_priorities || "Handover lodged").slice(0, 60)}`,
  },
];

const LS_KEY = "sl_signoff_inbox_seen";
const LS_KEY_DISMISSED = "sl_signoff_inbox_dismissed";

function readSetFor(key, userKey) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return new Set();
    const map = JSON.parse(raw);
    return new Set(map[userKey] || []);
  } catch { return new Set(); }
}
function writeSetFor(key, userKey, ids) {
  try {
    const raw = localStorage.getItem(key);
    const map = raw ? JSON.parse(raw) : {};
    map[userKey] = Array.from(ids);
    localStorage.setItem(key, JSON.stringify(map));
  } catch { /* ignore */ }
}

export function useSignoffInbox({ user, intervalMs = 20000 } = {}) {
  const userKey = user?.username || user?.id || "anon";
  const canSupervise = useMemo(() => {
    if (!user) return false;
    if (user.is_admin) return true;
    const r = String(user.role || "").toLowerCase();
    return SUP_ROLES.some((k) => r.includes(k));
  }, [user]);

  const [items, setItems] = useState([]);
  // Two independent sets — same reason email clients keep them separate:
  //   `seen`      = user has EYEBALLED the notification (drops the badge count,
  //                 stops the orange pulse) — item STAYS in the tray.
  //   `dismissed` = user has explicitly HIDDEN the notification by tapping
  //                 the × or by tapping VIEW on an informational alert.
  // Previously both jobs were done by a single "seen" set, so opening the
  // tray called markAllRead() which dumped every pending sign-off out of the
  // list. Supervisors would open the bell, watch their 5 pending sign-offs
  // vanish after 1.2s, and think the queue had been cleared.
  const [seen, setSeen] = useState(() => readSetFor(LS_KEY, userKey));
  const [dismissed, setDismissed] = useState(() => readSetFor(LS_KEY_DISMISSED, userKey));
  const [loading, setLoading] = useState(false);
  const runningRef = useRef(false);

  // Cross-device sync: hydrate the seen/dismissed sets from the portal
  // backend on mount so clearing on the laptop also clears on the phone.
  // Server state is the source of truth; localStorage is just a fast cache.
  useEffect(() => {
    if (!canSupervise || !user) return;
    let alive = true;
    (async () => {
      try {
        const { data } = await portalApi.get(`/api/notifications/state/${encodeURIComponent(userKey)}`);
        if (!alive || !data) return;
        const serverSeen = new Set(data.seen || []);
        const serverDismissed = new Set(data.dismissed || []);
        // Union local + server (local writes may have happened while offline).
        setSeen((prev) => {
          const merged = new Set([...prev, ...serverSeen]);
          writeSetFor(LS_KEY, userKey, merged);
          return merged;
        });
        setDismissed((prev) => {
          const merged = new Set([...prev, ...serverDismissed]);
          writeSetFor(LS_KEY_DISMISSED, userKey, merged);
          return merged;
        });
      } catch { /* silent — falls back to localStorage-only */ }
    })();
    return () => { alive = false; };
  }, [canSupervise, user, userKey]);

  const pushSeen = useCallback((nextSeen, nextDismissed) => {
    // Best-effort push to the shared portal backend (fire-and-forget).
    portalApi.post(`/api/notifications/state/${encodeURIComponent(userKey)}`, {
      seen: Array.from(nextSeen || []),
      dismissed: Array.from(nextDismissed || []),
    }).catch(() => { /* silent — localStorage is our fallback */ });
  }, [userKey]);

  const fetchAll = useCallback(async () => {
    if (!canSupervise) { setItems([]); return; }
    if (runningRef.current) return;
    if (typeof document !== "undefined" && document.visibilityState !== "visible") return;
    runningRef.current = true;
    setLoading(true);
    try {
      const results = await Promise.all(
        SOURCES.map(async (src) => {
          try {
            const { data } = await api.get(src.endpoint, { params: { limit: 30 } });
            const arr = Array.isArray(data) ? data : (data?.items || data?.results || []);            return arr
              .filter(src.needsSign)
              .map((x) => {
                const id = x.id || x._id || x.uid;
                return {
                  id: `${src.kind}:${id}`,
                  rid: id,
                  kind: src.kind,
                  label: src.label,
                  verb: src.verb,
                  route: src.route,
                  informational: !!src.informational,
                  subject: safe(src.subject(x)),
                  actor: safe(src.actor(x)) || "Someone",
                  ts: parseTs(src.time(x)),
                  status: x.status || x.stage || "",
                  priority: x.priority || x.severity || x.risk_level,
                  assignedTo: src.assignedTo ? src.assignedTo(x) : null,
                  assignedToName: src.assignedToName ? src.assignedToName(x) : null,
                  assignedToUsername: src.assignedToUsername ? src.assignedToUsername(x) : null,
                  raw: x,
                };
              });
          } catch { return []; }
        }),
      );
      const meId = user?.id || user?._id || null;
      const meUsername = String(user?.username || "").toLowerCase();
      const meName = String(user?.name || "").toLowerCase();
      // Strict tradesman → supervisor routing.
      //   • If an item has been routed to a specific supervisor (any of
      //     supervisor_id / assigned_supervisor_id / target_supervisor_id
      //     is populated) and that supervisor ISN'T me — HIDE it.
      //   • This applies to owners/admins too. If Josh signs off an
      //     inspection to Troy, only Troy sees it — nobody else, not even
      //     the site owner.
      //   • Unassigned items (no target supervisor at all) remain visible to
      //     every supervisor+ so nothing falls through the cracks.
      //   • Informational alerts (handovers) are not routed — always show.
      const isMine = (n) => {
        if (!n.assignedTo) return null; // no routing — separate branch
        if (meId && String(n.assignedTo) === String(meId)) return true;
        // Some backends stamp the username or the display name instead of a
        // UUID. Support all three so a routing decision isn't lost to a
        // schema oddity.
        const uname = String(n.assignedToUsername || "").toLowerCase();
        if (meUsername && uname && uname === meUsername) return true;
        const aname = String(n.assignedToName || "").toLowerCase();
        if (meName && aname && aname === meName) return true;
        return false;
      };
      const flat = results.flat()
        .filter((n) => n.rid)
        .filter((n) => {
          if (n.informational) return true;               // handovers — always show
          const mine = isMine(n);
          if (mine === null) return true;                 // unassigned — everyone sees
          return mine === true;                           // routed — only me
        })
        .sort((a, b) => {
          // Items directly assigned to me still float to the top (over any
          // unassigned items that also survived the filter).
          const aMine = a.assignedTo && isMine(a) ? 1 : 0;
          const bMine = b.assignedTo && isMine(b) ? 1 : 0;
          if (aMine !== bMine) return bMine - aMine;
          return b.ts - a.ts;
        });
      setItems(flat);
    } finally {
      runningRef.current = false;
      setLoading(false);
    }
    // user.id is the primary identity — the other user.* deps the linter
    // wants are derived display fields that don't affect routing decisions.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canSupervise, user?.id]);

  useEffect(() => {
    fetchAll();
    if (!intervalMs) return undefined;
    const iv = setInterval(fetchAll, intervalMs);
    const onVis = () => { if (document.visibilityState === "visible") fetchAll(); };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      clearInterval(iv);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [fetchAll, intervalMs]);

  // The tray only ever shows non-dismissed items. `seen` only affects the
  // badge count / pulse — dismissed items are the ones we actively hide.
  const visible = useMemo(() => items.filter((n) => !dismissed.has(n.id)), [items, dismissed]);
  const unread = useMemo(() => visible.filter((n) => !seen.has(n.id)), [visible, seen]);
  // Only ACTIONABLE items (sign-offs) drive the urgent orange badge + pulse.
  // Informational alerts (e.g. freshly-lodged handovers) still appear in the
  // list but don't count toward the "needs my signature" number — otherwise a
  // supervisor sees "1 pending sign-off" and thinks something is unsigned.
  const unreadActionable = useMemo(() => unread.filter((n) => !n.informational), [unread]);
  const unreadCount = unreadActionable.length;
  // Total notification count (sign-offs + alerts) — used by the dropdown badge
  // pill so supervisors can still see "there is something in the tray" even
  // when nothing needs signing.
  const alertCount = useMemo(() => unread.filter((n) => n.informational).length, [unread]);

  // Mark everything currently in the tray as READ (drops the badge) — but
  // items STAY in the list. Supervisors need to see their queue even after
  // opening the bell. Dismissal (hiding) is only done via the × button or
  // by tapping VIEW on informational alerts.
  const markAllRead = useCallback(() => {
    const next = new Set(seen);
    visible.forEach((n) => next.add(n.id));
    setSeen(next);
    writeSetFor(LS_KEY, userKey, next);
    pushSeen(next, dismissed);
  }, [visible, seen, dismissed, userKey, pushSeen]);

  const dismiss = useCallback((notifId) => {
    let nextDismissed;
    let nextSeen;
    setDismissed((prev) => {
      nextDismissed = new Set(prev);
      nextDismissed.add(notifId);
      writeSetFor(LS_KEY_DISMISSED, userKey, nextDismissed);
      return nextDismissed;
    });
    // Also mark seen so the badge count stays consistent.
    setSeen((prev) => {
      nextSeen = new Set(prev);
      nextSeen.add(notifId);
      writeSetFor(LS_KEY, userKey, nextSeen);
      return nextSeen;
    });
    // Fire-and-forget cross-device sync.
    setTimeout(() => pushSeen(nextSeen, nextDismissed), 0);
  }, [userKey, pushSeen]);

  return {
    items: visible,
    unread,
    unreadCount,
    alertCount,
    loading,
    canSupervise,
    markAllRead,
    dismiss,
    refresh: fetchAll,
  };
}

function safe(v) {
  if (v == null) return "";
  if (typeof v === "string") return v;
  return String(v);
}
function parseTs(v) {
  if (!v) return 0;
  const d = new Date(String(v).replace(" ", "T"));
  return isNaN(d) ? 0 : d.getTime();
}
